import React, { useEffect, useState } from 'react';

/* 💌 Open when… — six wax-sealed letters for the moments between visits.
   Reopenable any time; the first opening of each is remembered on the device
   so unopened seals glow. The words are Tanush's to edit — they live right
   here in LETTERS. */

const LS_OPENED = 'anusha_letters_opened';

const LETTERS = [
  {
    id: 'miss-me',
    seal: '💌',
    label: 'open when you miss me',
    lines: [
      'So you miss me. Good. (Sorry. But also — good. 😌)',
      'Here’s what I want you to do: close your eyes and replay us — the laughing-at-nothing, the almost-getting-caught glances, that thing where you fall asleep mid-sentence and I keep listening anyway.',
      'Now open them. Check your phone. There’s probably already a text from me, because let’s be honest — I missed you first.',
    ],
    sign: '— yours, always five minutes from calling you anyway',
  },
  {
    id: 'mad-at-me',
    seal: '🔥',
    label: 'open when you’re mad at me',
    lines: [
      'OFFICIAL STATEMENT FROM THE ACCUSED:',
      'I have reviewed the evidence and… yeah. I’m an idiot. A devoted idiot. An idiot who will show up with your favourite food and that face you can’t stay mad at (you know the one — I’ve been practising it in the mirror).',
      'You’re allowed to be mad. Take your time. I’ll be right here, holding the snacks, ready to apologise properly — twice.',
      'The defence rests. (On your shoulder. As soon as you let it.)',
    ],
    sign: '— your favourite idiot 🫠',
  },
  {
    id: 'cant-sleep',
    seal: '🌙',
    label: 'open when you can’t sleep',
    lines: [
      'Hey you. Still awake?',
      'Then come here, in your head: that terrace, sixty-four floors up. The whole city was glittering and showing off, and I swear I didn’t see a single light. I was too busy watching you.',
      'Breathe in for four, out for four. Imagine my hoodie. Imagine my heartbeat being annoyingly steady like always.',
      'Sleep now, baby. I’ll be in your dreams in five minutes — don’t start them without me.',
    ],
    sign: '— daddy’s got the night shift 😏🌙',
  },
  {
    id: 'doubt-yourself',
    seal: '🌻',
    label: 'open when you doubt yourself',
    lines: [
      'Anusha. Baby. Listen to me very carefully.',
      'You’re asking the wrong person if you expect an unbiased review — I am hopelessly, permanently compromised. But the facts stand:',
      'You walk into a room and the room improves. You’re smarter than you give yourself credit for, kinder than anyone I know, and you’ve survived 100% of your worst days so far. Undefeated, baby.',
      'And on the days you can’t believe in you — borrow my belief. I keep a full tank.',
    ],
    sign: '— your loudest fan (front row, forever)',
  },
  {
    id: 'need-a-laugh',
    seal: '😂',
    label: 'open when you need a laugh',
    lines: [
      'A short list of things I, a grown man, have done because of you:',
      '· Built an entire secret website instead of just texting “I love you” like a normal person.',
      '· Practised saying “hi” before our first date. Out loud. Multiple takes.',
      '· Lost a staring contest to your photo. It wasn’t close.',
      '· Made a “No” button run away from you because I literally could not accept the concept.',
      'I am not okay. I have never been more okay.',
    ],
    sign: '— the man, the myth, the menace 🧑‍🍳',
  },
  {
    id: 'random-tuesday',
    seal: '🍵',
    label: 'open on a random tuesday',
    lines: [
      'No occasion. That’s the whole point.',
      'It’s not your birthday. It’s not an anniversary. The calendar has nothing to declare. And I still picked today to remind you: I love you on the boring days too — maybe most on the boring days.',
      'Anyone can be romantic on the 14th. But Tuesday-love? That’s the real flex.',
      'Now go do whatever you were doing, but cuter, knowing somebody adores you.',
    ],
    sign: '— yours on every weekday ending in “y”',
  },
];

const readOpened = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LS_OPENED));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export default function Letters() {
  const [openId, setOpenId] = useState(null);
  const [opened, setOpened] = useState(readOpened);

  const open = (id) => {
    setOpenId(id);
    if (!opened.includes(id)) {
      const next = [...opened, id];
      setOpened(next);
      try {
        localStorage.setItem(LS_OPENED, JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
    }
  };

  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId]);

  const letter = LETTERS.find((l) => l.id === openId);

  return (
    <div className="aj-letters">
      <h3 className="aj-letters-title">letters for later 💌</h3>
      <p className="anusha-sub">
        sealed until you need them. no peeking ahead — the wax knows. 😏
      </p>

      <div className="aj-env-grid">
        {LETTERS.map((l) => (
          <button
            type="button"
            key={l.id}
            className={`aj-env${opened.includes(l.id) ? ' is-opened' : ''}`}
            onClick={() => open(l.id)}
          >
            <span className="aj-env-flap" aria-hidden="true" />
            <span className={`aj-env-seal${opened.includes(l.id) ? '' : ' is-glowing'}`} aria-hidden="true">
              {l.seal}
            </span>
            <span className="aj-env-label">{l.label}</span>
          </button>
        ))}
      </div>

      {letter && (
        <div className="aj-letter-overlay" onClick={() => setOpenId(null)}>
          <div className="aj-letter-paper" onClick={(e) => e.stopPropagation()}>
            <button
              type="button" className="aj-letter-close"
              onClick={() => setOpenId(null)} aria-label="close letter"
            >
              ×
            </button>
            <div className="aj-letter-head">
              <span className="aj-letter-seal" aria-hidden="true">{letter.seal}</span>
              <h4 className="aj-letter-title">{letter.label}</h4>
            </div>
            <div className="aj-letter-body">
              <p className="aj-letter-dear">Anusha baby,</p>
              {letter.lines.map((line) => (
                <p key={line.slice(0, 24)}>{line}</p>
              ))}
              <p className="aj-letter-sign">{letter.sign}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
